// The seven preset templates (contract section 3). Data, easy to change: a template only seeds a new preset.
// `show` is always written out in full (allowlist: a control the engine gains later stays hidden everywhere);
// `values` is sparse (only what differs from the registry defaults and the package). No template names a place,
// a tour or a season: those are the map's own and the client picks them after creating the preset.
import { freeze } from './registry.js';
/** @typedef {import('./typedefs.js').Template} Template */

/** @type {ReadonlyArray<Template>} */
export const TEMPLATES = freeze([
  {
    // The full map on the client's website: browse, pick, look around, change the time and the season.
    id: 'website-explorer', label: 'tpl.website-explorer', access: 'public',
    show: [
      'brand', 'season.switch', 'tour.button', 'language.switch', 'fullscreen',
      'explore.panel', 'explore.search', 'explore.categories',
      'time.dates', 'time.pickDate', 'time.hour', 'time.play',
      'aurora.toggle', 'aurora.panel', 'compass', 'home', 'trees',
      'map.pick', 'card.gallery', 'card.interior', 'card.booking', 'card.stepper',
    ],
    values: { 'lang.mode': 'visitor' },
  },
  {
    // A calm showpiece at the top of a page: a few controls, room types instead of unit numbers, small cards.
    // Maps 1:1 from the marketing hero profile (apps/site/public/demo/landing.json).
    id: 'homepage-hero', label: 'tpl.homepage-hero', access: 'public',
    show: ['tour.button', 'aurora.toggle', 'home', 'trees', 'map.pick', 'card.interior', 'card.booking'],
    values: { 'time.mode': 'fixed', 'season.mode': 'fixed', 'labels.unitNumbers': false, 'cards.size': 'compact', 'render.quality': 'high' },
  },
  {
    // One room type on its own page: the camera stays on the place in camera.focus, its card is the content.
    id: 'room-page', label: 'tpl.room-page', access: 'public',
    show: ['fullscreen', 'card.gallery', 'card.interior', 'card.booking', 'card.stepper'],
    values: { 'camera.rule': 'focus', 'time.mode': 'fixed', 'season.mode': 'fixed' },
  },
  {
    // A screen in the lobby: nothing to press, the playlist loops, the side panel tells what is on screen.
    id: 'lobby-tv-loop', label: 'tpl.lobby-tv-loop', access: 'public',
    show: ['brand'],
    values: {
      'layout.id': 'lobby-panel', 'camera.rule': 'drift', 'tours.autoplay': true, 'tours.loop': true,
      'time.mode': 'real', 'season.mode': 'calendar', 'booking.show': false, 'render.quality': 'high',
    },
  },
  {
    // The team's working map: everything. Private by default. (The client's own layers are the view's `layers.on`.)
    id: 'staff-map', label: 'tpl.staff-map', access: 'members',
    show: [
      'brand', 'season.switch', 'language.switch', 'fullscreen',
      'explore.panel', 'explore.search', 'explore.categories',
      'time.dates', 'time.pickDate', 'time.hour', 'time.play',
      'aurora.toggle', 'compass', 'home', 'trees',
      'map.pick', 'card.gallery', 'card.interior', 'card.stepper',
    ],
    values: { 'lang.mode': 'visitor', 'booking.show': false },
  },
  {
    // A guest on site with a phone (often from a QR code): find a place, today's light, no selling.
    id: 'guest-wayfinding', label: 'tpl.guest-wayfinding', access: 'public',
    show: [
      'language.switch', 'explore.panel', 'explore.search', 'explore.categories',
      'compass', 'home', 'locate', 'wayfind', 'map.pick', 'card.gallery', 'card.stepper', // WS-91: + where the guest is, + "Where to?"
    ],
    values: { 'lang.mode': 'visitor', 'time.mode': 'real', 'season.mode': 'calendar', 'booking.show': false },
  },
  {
    // Nothing shown, nothing changed: the map alone, to build a preset from scratch.
    id: 'blank', label: 'tpl.blank', access: 'public',
    show: [],
    values: {},
  },
]);
